// src/pages/Dashboard.jsx
import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea,
  Box,
  Skeleton,
  LinearProgress,
  Chip,
  Divider,
  Button
} from '@mui/material';
import { FolderOpen, Description, DateRange, CheckCircle } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';

function Dashboard() {
  const { user, loading: authLoading } = useContext(AuthContext);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  
  // Refs for managing state and preventing race conditions
  const isLoadingData = useRef(false);
  const lastVisibilityChange = useRef(Date.now());
  const cachedProjects = useRef(null);
  const globalTimeoutId = useRef(null);
  const fetchInProgress = useRef(false);

  // Store projects in cache when we get fresh data
  useEffect(() => {
    if (projects.length > 0) {
      cachedProjects.current = projects;
    }
  }, [projects]);

  // Helper function to reset connection state
  const resetConnection = useCallback(() => {
    isLoadingData.current = false;
    fetchInProgress.current = false;
  }, []);

  // Helper function to fallback to cache if available
  const useCachedDataIfAvailable = useCallback(() => {
    if (cachedProjects.current && cachedProjects.current.length > 0) {
      setProjects(cachedProjects.current);
      return true;
    }
    return false;
  }, []);

  const fetchUserProfile = useCallback(async (isMountedCheck, userId) => {
    try {
      if (!userId || !isMountedCheck()) return;
      
      const { data, error } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', userId)
          .single();

      if (error) throw error;
      if (isMountedCheck()) {
        setUserProfile(data);
      }
      return data;
    } catch (error) {
      console.error('[fetchUserProfile] Error:', error);
      return null;
    }
  }, []);

  const fetchUserProjects = useCallback(async (isMountedCheck) => {
    try {
      if (!isMountedCheck()) return;

      const { data, error } = await supabase
          .from('projects')
          .select(`
            id,
            name,
            created_at,
            documents:documents(
              id,
              status
            )
          `)
          .neq('status', 'archived')
          .order('created_at', { ascending: false });

      if (error) {
        console.error("[fetchUserProjects] Supabase error:", error);
        throw error;
      }

      if (isMountedCheck()) {
        const transformedData = data?.map(project => {
          const documents = project.documents || [];
          const totalDocuments = documents.length;
          const assessedDocuments = documents.filter(doc => doc.status === 'reviewed' || doc.status === 'exported').length;
          
          return {
            ...project,
            documentCount: totalDocuments,
            assessedCount: assessedDocuments,
            assessmentProgress: totalDocuments > 0 ? (assessedDocuments / totalDocuments) * 100 : 0
          };
        }) || [];
        
        setProjects(transformedData);
        return transformedData;
      }
    } catch (error) {
      console.error('[fetchUserProjects] Error:', error);
      return [];
    }
  }, []);

  // Function to trigger data refresh - Simplified
  const refreshData = useCallback(() => {
      console.log("Manual refresh triggered (currently relies on main useEffect reacting to user state)");
  }, []);

  // Main data loading effect
  useEffect(() => {
    let isMounted = true;
    const isMountedCheck = () => isMounted;
    let loadTimeout = null;

    const loadData = async () => {
      if (isLoadingData.current || fetchInProgress.current || authLoading || !user) {
        if (!user && !authLoading) {
           setProjectsLoading(false);
        }
        return;
      }

      try {
        isLoadingData.current = true;
        fetchInProgress.current = true;
        
        const hasCachedData = useCachedDataIfAvailable();
        
        if (isMountedCheck() && !hasCachedData) {
          setProjectsLoading(true);
        }
        
        if (globalTimeoutId.current) {
          clearTimeout(globalTimeoutId.current);
        }
        
        globalTimeoutId.current = setTimeout(() => {
          console.error("⚠️ Global loadData timeout reached! Forcing release of loading state.");
          if (isMountedCheck()) {
            setProjectsLoading(false);
          }
          resetConnection();
        }, 15000);

        try {
          await fetchUserProfile(isMountedCheck, user.id);
        } catch (profileError) {
        }

        if (isMountedCheck()) {
          try {
            await fetchUserProjects(isMountedCheck);
          } catch (projectsError) {
            console.error("Error in projects fetch:", projectsError);
          }
        }
      } catch (error) {
        console.error("Error during sequential loadData:", error);
      } finally {
        if (globalTimeoutId.current) {
          clearTimeout(globalTimeoutId.current);
          globalTimeoutId.current = null;
        }
        
        if (isMountedCheck()) {
          setProjectsLoading(false);
        }
        
        isLoadingData.current = false;
        fetchInProgress.current = false;
      }
    };

    loadData();

    const handleVisibilityChangeInternal = () => {
       if (document.visibilityState === 'visible') {
         const now = Date.now();
         const timeSinceLastChange = now - lastVisibilityChange.current;
         if (timeSinceLastChange > 1000 && !isLoadingData.current && !fetchInProgress.current) {
           lastVisibilityChange.current = now;
           loadData();
         }
       }
     };

    // document.addEventListener('visibilitychange', handleVisibilityChangeInternal); // Uitgecommentarieerd

    return () => {
      isMounted = false;
      // document.removeEventListener('visibilitychange', handleVisibilityChangeInternal); // Uitgecommentarieerd
      if (globalTimeoutId.current) {
        clearTimeout(globalTimeoutId.current);
        globalTimeoutId.current = null;
      }
      isLoadingData.current = false;
      fetchInProgress.current = false;
    };
  }, [user, authLoading, fetchUserProfile, fetchUserProjects, resetConnection, useCachedDataIfAvailable]); 

  // Overall loading combines auth and projects loading
  const combinedLoading = authLoading || projectsLoading;

  if (!user && !authLoading) {
    // If auth check is complete and still no user, redirect to login
    navigate('/login');
    return null;
  }

  return (
    <Container
      component={motion.div}
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{ mt: 4, mb: 4 }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 4 },
          mb: 4,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: theme.palette.primary.contrastText,
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Welkom, {userProfile?.full_name || user?.email}!
        </Typography>
        <Typography variant="subtitle1">
          Hier is een overzicht van je recente projecten.
        </Typography>
      </Paper>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
        Mijn Projecten
      </Typography>

      {combinedLoading ? (
        <Grid container spacing={3}>
          {[...Array(3)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid item xs={12} sm={6} md={4} key={project.id}>
              <motion.div whileHover={{ y: -5 }} transition={{ type: 'spring', stiffness: 300 }}>
                <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                  <CardActionArea onClick={() => navigate(`/projects/${project.id}`)} sx={{ flexGrow: 1, p: 2 }}>
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FolderOpen sx={{ mr: 1.5, color: 'primary.main' }} />
                        <Typography variant="h6" component="h3" noWrap title={project.name}>
                    {project.name}
                      </Typography>
                    </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 2 }}>
                        <DateRange sx={{ mr: 1.5, fontSize: '1.25rem' }} />
                        <Typography variant="body2">
                          Aangemaakt: {new Date(project.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={1} sx={{ textAlign: 'center' }}>
                        <Grid item xs={6}>
                          <Typography variant="h6" component="p">{project.documentCount}</Typography>
                          <Typography variant="caption" color="text.secondary">Documenten</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="h6" component="p">{project.assessedCount}</Typography>
                          <Typography variant="caption" color="text.secondary">Beoordeeld</Typography>
                        </Grid>
                      </Grid>

                      <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={project.assessmentProgress} 
                          sx={{ height: 8, borderRadius: 4 }}
                      />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      )}

      {!combinedLoading && projects.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, mt: 4, textAlign: 'center', backgroundColor: 'transparent' }}>
          <Typography variant="h6" gutterBottom>
            Nog geen projecten gevonden.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/projects/new')}>
            Start je eerste project
          </Button>
        </Paper>
      )}
    </Container>
  );
}

export default Dashboard;
